import React, { useMemo } from 'react';

import { usePermissions } from '@/modules/common';
import { EntityType } from '@/modules/common/types/entity-type';
import { OpsiumPolicy } from '@/modules/common/types/opsium-policy';
import { useConnectedEntityIntegrations } from '@/modules/onboarding/common/hooks/use-connected-entity-integrations';
import { EntityReconciliationConfig } from '@/modules/onboarding/common/types/entity-reconciliation-config';
import { invalidateOnboardingProjectQueries } from '@/modules/onboarding/common/utils/onboarding-query-invalidation';

import {
  deleteProjects,
  getProjects,
  getProjectsValidationReport,
  submitProjects,
  updateProjectsBulk,
} from '../api/projects';
import { AddOnboardingProjectsModal } from '../components/add-projects-modal/add-onboarding-projects-modal';
import { ImportOnboardingProjectsModal } from '../components/import-projects-modal/import-onboarding-projects-modal';
import { ResolveOnboardingProjectsConflictsModal } from '../components/resolve-conflicts-modal/resolve-onboarding-projects-conflicts-modal';
import { getProjectsSchema } from '../constants';
import { getProjectsColumns, ProjectColumnDeps } from '../constants/columns';
import { useClientSelectLoaders } from '../hooks/use-client-select-loaders';
import { ProjectFieldMapper, ProjectFieldValues } from '../mappers/project-field-mapper';
import { Project, ProjectDataReconciliationQueryKeys, ProjectsFormData } from '../types';

const PROJECTS_I18N_NAMESPACE = 'authenticated.onboarding.projects';

export function useProjectsReconciliationConfig(): EntityReconciliationConfig<
  Project,
  ProjectsFormData,
  ProjectFieldValues
> {
  const { integrations: connectedIntegrations } = useConnectedEntityIntegrations(
    EntityType.Project
  );

  return {
    entityKey: 'projects',
    tableId: 'onboarding-projects',
    integrationColumnsCount: connectedIntegrations.length,

    i18nNamespaces: ['common', 'authenticated.onboarding.common', PROJECTS_I18N_NAMESPACE],
    i18nKeys: {
      searchPlaceholder: `${PROJECTS_I18N_NAMESPACE}:filters.searchPlaceholder`,
      searchResultAll: `${PROJECTS_I18N_NAMESPACE}:searchResult.all`,
      searchResultCount: `${PROJECTS_I18N_NAMESPACE}:searchResult.count`,
      searchResultSelected: `${PROJECTS_I18N_NAMESPACE}:searchResult.selected`,
      deleteButton: `${PROJECTS_I18N_NAMESPACE}:delete`,
      clearSelection: `${PROJECTS_I18N_NAMESPACE}:clearSelection`,
      warningsModalTitle: `${PROJECTS_I18N_NAMESPACE}:warningsModal.title`,
      warningsModalText: `${PROJECTS_I18N_NAMESPACE}:warningsModal.text`,
      warningsModalConfirm: `${PROJECTS_I18N_NAMESPACE}:warningsModal.confirm`,
      warningsModalCancel: `${PROJECTS_I18N_NAMESPACE}:warningsModal.cancel`,
      noDataYet: `${PROJECTS_I18N_NAMESPACE}:noDataYet`,
      noDataDescription: `${PROJECTS_I18N_NAMESPACE}:noDataDescription`,
      submitEntitiesWithErrorsExist: `${PROJECTS_I18N_NAMESPACE}:submit.entitiesWithErrorsExist`,
      submitDuplicateEntitiesExists: `${PROJECTS_I18N_NAMESPACE}:submit.duplicateEntitiesExists`,
      deleteErrorFallback: `${PROJECTS_I18N_NAMESPACE}:api.error.deleteFailed`,
      updateErrorFallback: `${PROJECTS_I18N_NAMESPACE}:api.error.updateFailed`,
      deleteModalTitle: `${PROJECTS_I18N_NAMESPACE}:deleteModal.title`,
      deleteModalText: `${PROJECTS_I18N_NAMESPACE}:deleteModal.text`,
      deleteModalCancel: `${PROJECTS_I18N_NAMESPACE}:deleteModal.cancel`,
      deleteModalConfirm: `${PROJECTS_I18N_NAMESPACE}:deleteModal.confirm`,
      deleteModalAndMore: `${PROJECTS_I18N_NAMESPACE}:deleteModal.andMore`,
    },

    queryKeys: {
      entities: ProjectDataReconciliationQueryKeys.Projects,
      validationReport: ProjectDataReconciliationQueryKeys.ValidationReport,
    },
    getEntities: getProjects,
    getValidationReport: getProjectsValidationReport,

    getSchema: () => getProjectsSchema(),
    defaultFormValues: { projects: [] } as unknown as ProjectsFormData,
    formFieldPrefix: 'projects',
    getColumns: ({ form, getFormIndex, extraDeps }) =>
      getProjectsColumns({ form, getFormIndex, deps: extraDeps as ProjectColumnDeps }),

    duplicateFieldExtractors: [(item: { name: string }) => item.name],

    fieldMapper: new ProjectFieldMapper(),
    buildUpdateRequestItem: (entityId, changes) => ({
      id: entityId,
      name: changes.name,
      clientId: changes.clientId ? parseInt(changes.clientId, 10) : 0,
    }),

    deleteEntities: ids => deleteProjects({ ids }),
    updateEntitiesBulk: updateProjectsBulk,
    submitEntities: submitProjects,
    invalidateQueries: invalidateOnboardingProjectQueries,

    renderAddButton: onSuccess => React.createElement(AddOnboardingProjectsModal, { onSuccess }),
    renderImportButton: onSuccess =>
      React.createElement(ImportOnboardingProjectsModal, { onSuccess }),
    renderResolveDuplicatesModal: (selectedItems, onSuccess, options) =>
      React.createElement(ResolveOnboardingProjectsConflictsModal, {
        selectedItems,
        onSuccess,
        connectedIntegrations,
        disabled: options?.disabled,
        disabledTooltip: options?.disabledTooltip,
      }),

    useExtraColumnDeps: (): ProjectColumnDeps => {
      const clientLoaders = useClientSelectLoaders();
      return useMemo(
        () => ({ clientLoaders, connectedIntegrations }),
        [clientLoaders, connectedIntegrations]
      );
    },

    usePermissionChecks: () => {
      const { hasPermission } = usePermissions();
      return {
        canCreate: hasPermission(OpsiumPolicy.ProjectsCreate),
        canEdit: hasPermission(OpsiumPolicy.ProjectsEdit),
        canDelete: hasPermission(OpsiumPolicy.ProjectsDelete),
      };
    },
  };
}
